import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import I18n from 'I18n'; // @ts-expect-error untyped dependency

import localeComparator from 'I18n/utils/localeComparator';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Small from 'UIComponents/elements/Small';
import UIExpandableSection from 'UIComponents/accordion/UIExpandableSection';
import UITile from 'UIComponents/tile/UITile';
import UIAlert from 'UIComponents/alert/UIAlert';
import Categories from 'ContentUtils/constants/Categories';
import TemplateTypes from 'ContentUtils/constants/TemplateTypes';
import BigText from 'marketplace-ui-common/components/elements/BigText'; // @ts-expect-error untyped dependency

import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper'; // @ts-expect-error untyped dependency

import { getIsUngatedForContentTypes } from '../../selectors/auth';

const ErrorComponent = () => {
  return /*#__PURE__*/_jsx(UIAlert, {
    type: "danger",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.themes.details.themeContents.error"
    })
  });
};

const ThemeContentSectionWrapper = styled.div.withConfig({
  displayName: "ThemeContents__ThemeContentSectionWrapper",
  componentId: "sc-12qanmi-0"
})(["width:100%;"]);
const ThemeContentItemWrapper = styled.div.withConfig({
  displayName: "ThemeContents__ThemeContentItemWrapper",
  componentId: "sc-12qanmi-1"
})(["border-bottom:solid;border-width:1px;border-color:", ";padding:", ";padding-left:44px;"], GREAT_WHITE, props => props.hasHelpText ? '12px' : '16px');

function ThemeModuleItemWrapper({
  item
}) {
  const isUngatedForContentTypes = useSelector(getIsUngatedForContentTypes);
  const computedContentTypes = useMemo(() => {
    if (isUngatedForContentTypes) {
      return item.contentTypes.slice(0);
    }

    const contentTypes = [];
    item.hostTemplateTypes.forEach(type => {
      switch (type) {
        case 'PAGE':
          {
            contentTypes.push('LANDING_PAGE', 'SITE_PAGE');
            break;
          }

        case 'QUOTE':
          {
            contentTypes.push('QUOTE_TEMPLATE');
            break;
          }

        case 'ANY':
        case 'BLOG_LISTING':
        case 'BLOG_POST':
        case 'EMAIL':
        case 'KNOWLEDGE_BASE':
          {
            contentTypes.push(type);
            break;
          }

        default:
          break;
      }
    });
    return contentTypes;
  }, [isUngatedForContentTypes, item]);
  const isCompatibleWithAny = computedContentTypes.includes('ANY');
  const isCompatibleWithNone = !computedContentTypes.length;
  return /*#__PURE__*/_jsxs(ThemeContentItemWrapper, {
    children: [item.label, /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsxs(Small, {
      use: "help",
      children: [isCompatibleWithAny && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.themeContents.worksWith",
        options: {
          contentTypes: I18n.text('marketplace-assets-ui.themes.details.themeContents.contentTypes.ANY')
        }
      }), isCompatibleWithNone && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.themeContents.notCompatible"
      }), !isCompatibleWithAny && !isCompatibleWithNone && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.themeContents.worksWith",
        options: {
          contentTypes: I18n.formatList(computedContentTypes.map(templateType => I18n.text(`marketplace-assets-ui.themes.details.themeContents.contentTypes.${templateType}`)))
        }
      })]
    })]
  });
}

function ExpandableContentsSection({
  ItemWrapper = ThemeContentItemWrapper,
  message,
  contents,
  trackInteraction
}) {
  return /*#__PURE__*/_jsx(UIExpandableSection, {
    ContentWrapper: ThemeContentSectionWrapper,
    onOpenChange: ({
      target: {
        value: open
      }
    }) => {
      const action = open ? 'expand contents' : 'collapse contents';
      trackInteraction(action);
    },
    style: {
      marginTop: -1
    },
    titleText: /*#__PURE__*/_jsx(BigText, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: message,
        options: {
          count: contents.length
        }
      })
    }),
    use: "enclosed",
    children: contents.map(contentItem => {
      return /*#__PURE__*/_jsx(ItemWrapper, {
        item: contentItem,
        children: contentItem.label
      }, contentItem.label);
    })
  });
}

const isSystemTemplate = contentItem => {
  const templateType = TemplateTypes.byName[contentItem.templateType];
  return contentItem.fileType === 'TEMPLATE' && templateType && templateType.categoryId === Categories.unmapped.id;
};

const isSectionTemplate = contentItem => {
  return contentItem.fileType === 'TEMPLATE' && contentItem.templateType === 'section';
};

export default function ThemeContents({
  onTrackInteraction,
  requestState,
  themeContents
}) {
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    if (onTrackInteraction) {
      onTrackInteraction(action, Object.assign({}, eventProperties, {
        section: 'contents'
      }));
    }
  }, [onTrackInteraction]);
  const {
    TEMPLATE: pageTemplates = [],
    SYSTEM_TEMPLATE: systemTemplates = [],
    MODULE: modules = [],
    SECTION: sections = []
  } = useMemo(() => {
    return themeContents.contents.reduce((final, contentItem) => {
      let {
        fileType
      } = contentItem;

      if (fileType === 'RAW_ASSET' || !contentItem.label) {
        return final;
      }

      if (isSystemTemplate(contentItem)) {
        fileType = 'SYSTEM_TEMPLATE';
      }

      if (isSectionTemplate(contentItem)) {
        fileType = 'SECTION';
      }

      const fileTypeItems = final[fileType] = final[fileType] || [];
      fileTypeItems.push(contentItem);
      return final;
    }, {});
  }, [themeContents]);
  const sortedPageTemplates = useMemo(() => {
    return pageTemplates.sort((a, b) => localeComparator(a.label, b.label));
  }, [pageTemplates]);
  const sortedSystemTemplates = useMemo(() => {
    return systemTemplates.sort((a, b) => localeComparator(a.label, b.label));
  }, [systemTemplates]);
  const sortedModules = useMemo(() => {
    return modules.sort((a, b) => localeComparator(a.label, b.label));
  }, [modules]);
  const sortedSections = useMemo(() => {
    return sections.sort((a, b) => localeComparator(a.label, b.label));
  }, [sections]);
  return /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: requestState,
    ErrorComponent: ErrorComponent,
    children: /*#__PURE__*/_jsxs(UITile, {
      compact: true,
      children: [pageTemplates.length > 0 && /*#__PURE__*/_jsx(ExpandableContentsSection, {
        contents: sortedPageTemplates,
        message: "marketplace-assets-ui.themes.details.themeContents.pageTemplatesTitle",
        trackInteraction: action => {
          trackInteraction(action, {
            value: 'page templates'
          });
        }
      }), systemTemplates.length > 0 && /*#__PURE__*/_jsx(ExpandableContentsSection, {
        contents: sortedSystemTemplates,
        message: "marketplace-assets-ui.themes.details.themeContents.systemTemplatesTitle",
        trackInteraction: action => {
          trackInteraction(action, {
            value: 'system templates'
          });
        }
      }), sections.length > 0 && /*#__PURE__*/_jsx(ExpandableContentsSection, {
        contents: sortedSections,
        message: "marketplace-assets-ui.themes.details.themeContents.sectionsTitle",
        trackInteraction: action => {
          trackInteraction(action, {
            value: 'sections'
          });
        }
      }), modules.length > 0 && /*#__PURE__*/_jsx(ExpandableContentsSection, {
        contents: sortedModules,
        ItemWrapper: ThemeModuleItemWrapper,
        message: "marketplace-assets-ui.themes.details.themeContents.modulesTitle",
        trackInteraction: action => {
          trackInteraction(action, {
            value: 'modules'
          });
        }
      })]
    })
  });
}